import { ExchanegeRateService } from '@acme/api';
import { Icon } from '@acme/assets';
import {
  ClientExchangeRateResponse,
  EExchangeRateStatusEnum,
  ExchangeRateFilter,
  PagedClientExchangeRateResponse,
} from '@acme/domains';
import { Separator, useModal } from '@acme/foundation';
import {
  Button,
  ButtonGroup,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableRow,
} from 'flowbite-react';
import { useCallback, useEffect, useState } from 'react';
import { HiX } from 'react-icons/hi';
import { formatDate } from '../lib';
import { ActionMenuButton } from './actionMenuButton';
import { ExchangeRateForm } from './exchangeRateForm';
import { EFilterMode, Filter } from './filter';
import { NoData } from './noData';
import { RateHistoryTable } from './rateHistoryTable';

interface ExchangeRateTableProps {
  loader: (
    filter: ExchangeRateFilter
  ) => Promise<PagedClientExchangeRateResponse | undefined>;
  loading?: boolean;
  onRowClick?: (currencyId: string) => void;
  filterMode?: EExchangeTableState;
  tableMode?: EExchangeTableMode;
}

export enum EExchangeTableMode {
  General = 1,
  My,
}

export enum EExchangeTableState {
  Buy = 1,
  Sell,
}

export const ExchangeRateTable = ({
  loading,
  loader,
  onRowClick,
  filterMode,
  tableMode = EExchangeTableMode.General,
}: ExchangeRateTableProps) => {
  const { openModal, closeModal } = useModal();

  const [tableState, setTableState] = useState(
    filterMode || EExchangeTableState.Buy
  );
  const [data, setData] = useState<PagedClientExchangeRateResponse | null>();
  const [filter, setFilter] = useState({});

  const loadData = useCallback(async () => {
    const response = await loader({
      ...filter,
      type: tableState === EExchangeTableState.Buy ? 100 : 200,
      organziationFilter: tableMode === EExchangeTableMode.My ? 100 : 200,
    });
    setData(response);
  }, [filter, tableState, tableMode]);

  useEffect(() => {
    loadData();
  }, [filter, tableState]);

  const handleAddCurrencyClick = () => {
    openModal({
      content: (
        <ExchangeRateForm
          onClose={closeModal}
          onSubmit={ExchanegeRateService.postApiV1ExchanegeRateCreate}
        />
      ),
      size: 'xl',
      modalType: 'custom',
    });
  };

  const loadRateHistory = async (rateId: string) => {
    const res = await ExchanegeRateService.getApiV1ExchanegeRateHistory({
      rateId,
    });
    return res.data;
  };

  const handleApproveClick = (rowData: ClientExchangeRateResponse) => {
    openModal({
      content: (
        <ExchangeRateForm
          onClose={closeModal}
          onSubmit={ExchanegeRateService.postApiV1ExchanegeRateCreate}
          defaultData={rowData}
        />
      ),
      size: 'xl',
      modalType: 'custom',
    });
  };

  const onRateStoryClick = async (
    rateId: string | null | undefined,
    currencyPair: string
  ) => {
    const ratesHistory = await loadRateHistory(rateId as string);
    openModal({
      content: (
        <>
          <div className="flex pb-5">
            <h3 className="text-gray-900 text-lg font-semibold">
              {currencyPair}
            </h3>
            <button
              onClick={closeModal}
              className="text-gray-400 hover:text-gray-600 ml-auto"
            >
              <HiX className="h-5 w-5" />
            </button>
          </div>
          <Separator className="-mx-5" />
          <div className="overflow-auto max-h-[387px]">
            <RateHistoryTable data={ratesHistory ?? []} />
          </div>
        </>
      ),
      size: '6xl',
      modalType: 'custom',
    });
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-center py-8">
          <Spinner size="lg" />
          <span className="ml-3 text-gray-600">Загрузка курсов валют...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <div className="flex flex-col px-4">
        {!filterMode && (
          <div className="flex items-center gap-4 py-4">
            <p className="text-base font-semibold text-gray-500">Показывать:</p>
            <ButtonGroup>
              <Button
                color="primary"
                outline
                onClick={() => setTableState(EExchangeTableState.Buy)}
              >
                Покупка
              </Button>
              <Button
                color="primary"
                outline
                onClick={() => setTableState(EExchangeTableState.Sell)}
              >
                Продажа
              </Button>
            </ButtonGroup>
            {tableMode === EExchangeTableMode.My && (
              <Button
                color="primary"
                className="ml-auto flex gap-2"
                onClick={handleAddCurrencyClick}
              >
                Добавить валюту
                <Icon name="plus-outline" color="#FFFFFF" />
              </Button>
            )}
          </div>
        )}
        {tableMode === EExchangeTableMode.General && (
          <Filter
            mode={
              tableState === EExchangeTableState.Buy
                ? EFilterMode.ALL_FIELDS
                : EFilterMode.VOLUME_SEARCH
            }
            onChange={setFilter}
          />
        )}
      </div>

      {data?.items?.length === 0 ? (
        <NoData
          title={
            Object.keys(filter).length === 0
              ? 'Курсы пока не установлены'
              : 'Курсы по заданным условиям не найдены'
          }
        />
      ) : (
        <div className="overflow-x-auto">
          <Table hoverable>
            <TableHead>
              <TableRow>
                {tableMode === EExchangeTableMode.My ? (
                  <>
                    <TableHeadCell></TableHeadCell>
                    <TableHeadCell></TableHeadCell>
                  </>
                ) : (
                  <TableHeadCell>Наименование контрагента</TableHeadCell>
                )}
                <TableHeadCell>Валютная пара</TableHeadCell>
                <TableHeadCell>Объем валюты</TableHeadCell>
                <TableHeadCell>Объем</TableHeadCell>
                <TableHeadCell>Диапазон объема</TableHeadCell>
                <TableHeadCell>Время обновления</TableHeadCell>
                <TableHeadCell>
                  {tableMode === EExchangeTableMode.My
                    ? 'Время действия до'
                    : 'Количество успешных сделок'}
                </TableHeadCell>
              </TableRow>
            </TableHead>

            <TableBody className="divide-y">
              {data?.items?.map((rate) => (
                <TableRow
                  key={rate.rangeId}
                  onClick={() => onRowClick?.(rate.rangeId || '')}
                  className={`text-sm font-normal text-gray-900 dark:text-white ${rate.status === EExchangeRateStatusEnum.Blocked && 'bg-red-50 border-red-100 hover:bg-red-100'} 
                  ${rate.status === EExchangeRateStatusEnum.IsEditedByNotOwner && 'bg-yellow-50 border-yellow-100 hover:bg-yellow-100'}`}
                >
                  {tableMode === EExchangeTableMode.My ? (
                    <>
                      <TableCell className="w-[60px] text-gray-500">
                        <button
                          onClick={() =>
                            onRateStoryClick(
                              rate.rateId,
                              `${rate.cryptoCurrency?.code} > ${rate.currency?.code}`
                            )
                          }
                        >
                          <Icon name="clock-outline" />
                        </button>
                      </TableCell>
                      <TableCell className="w-[60px] flex px-0 flex-wrap justify-center text-gray-500">
                        {rate.status === EExchangeRateStatusEnum.Blocked ? (
                          <Button
                            size="sm"
                            color="gray"
                            className="px-2 bg-transparent text-gray-500"
                            onClick={() => handleApproveClick(rate)}
                          >
                            <Icon name="edit-outline" />
                          </Button>
                        ) : (
                          <ActionMenuButton rowData={rate} reload={loadData} />
                        )}
                      </TableCell>
                    </>
                  ) : (
                    <TableCell className="whitespace-nowrap">
                      {rate.organization?.name}
                    </TableCell>
                  )}
                  <TableCell className="whitespace-nowrap">
                    {`${rate.cryptoCurrency?.code} > ${rate.currency?.code}`}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {`${rate.amount} ${rate.cryptoCurrency?.code}`}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {`${rate.price} ${rate.currency?.code}`}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {`От ${rate.rangeMin} до ${rate.rangeMax}`}
                  </TableCell>
                  <TableCell className=" whitespace-nowrap">
                    {formatDate(rate.updatedAt)}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {tableMode === EExchangeTableMode.My
                      ? formatDate(rate.expireAt)
                      : rate.dealsCount}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};
